import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Textarea } from '../components/ui/textarea';
import { getInitials } from '../lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { toast } from 'sonner';
import { 
  Users, 
  UserPlus, 
  Mail, 
  Search,
  ChevronDown,
  ChevronRight,
  Clock,
  Circle,
  Building2,
  Filter,
  X,
  Briefcase,
  FolderKanban
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Team = () => {
  const { api, user } = useAuth();
  const { t } = useTranslation();
  const [organization, setOrganization] = useState(null);
  const [members, setMembers] = useState([]);
  const [workingMembers, setWorkingMembers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canInvite, setCanInvite] = useState(false);
  
  // UI State
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showCreateOrgDialog, setShowCreateOrgDialog] = useState(false);
  const [showProfileSheet, setShowProfileSheet] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Department collapse state
  const [collapsedDepts, setCollapsedDepts] = useState({});
  
  // Form state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('employee');
  const [inviteMessage, setInviteMessage] = useState('');
  const [orgName, setOrgName] = useState('');

  const COLORS = ['#3b82f6', '#6366f1', '#8b5cf6', '#ec4899', '#f97316', '#22c55e', '#14b8a6', '#f59e0b'];

  useEffect(() => {
    fetchData();
    // Refresh working members every 30 seconds
    const interval = setInterval(fetchWorkingMembers, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [orgRes, membersRes, workingRes, deptRes, inviteRes] = await Promise.all([
        api.get('/organizations'),
        api.get('/team/members').catch(() => ({ data: [] })),
        api.get('/team/working').catch(() => ({ data: [] })),
        api.get('/team/departments').catch(() => ({ data: [] })),
        api.get('/team/can-invite').catch(() => ({ data: { can_invite: false } }))
      ]);
      
      setOrganization(orgRes.data);
      setMembers(membersRes.data);
      setWorkingMembers(workingRes.data);
      setDepartments(deptRes.data);
      setCanInvite(inviteRes.data.can_invite);
    } catch (error) {
      console.error('Error fetching team data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkingMembers = async () => {
    try {
      const res = await api.get('/team/working');
      setWorkingMembers(res.data);
    } catch (error) {
      console.error('Error fetching working members:', error);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Email is required');
      return;
    }

    try {
      await api.post('/organizations/invite', {
        email: inviteEmail,
        role: inviteRole,
        message: inviteMessage
      });
      toast.success('Invitation sent');
      setShowInviteDialog(false);
      setInviteEmail('');
      setInviteRole('employee');
      setInviteMessage('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    }
  };

  const handleCreateOrg = async () => {
    if (!orgName.trim()) {
      toast.error('Organization name is required');
      return;
    }

    try {
      await api.post('/organizations', { name: orgName });
      toast.success('Organization created');
      setShowCreateOrgDialog(false);
      setOrgName('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create organization');
    }
  };

  const handleViewProfile = async (member) => {
    try {
      const res = await api.get(`/team/members/${member.id}/profile`);
      setSelectedMember(res.data);
      setShowProfileSheet(true);
    } catch (error) {
      toast.error('Failed to load profile');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online': return 'bg-emerald-50 dark:bg-emerald-900/30';
      case 'away': return 'bg-amber-50 dark:bg-amber-900/30';
      default: return 'bg-slate-400';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'online': return 'Online';
      case 'away': return 'Away';
      default: return 'Offline';
    }
  };

  const formatElapsedTime = (startTime) => {
    const start = new Date(startTime);
    const now = new Date();
    const diff = Math.floor((now - start) / 1000);
    const hours = Math.floor(diff / 3600);
    const minutes = Math.floor((diff % 3600) / 60);
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const getColorForIndex = (index) => COLORS[index % COLORS.length];

  // Filter members
  const filteredMembers = useMemo(() => {
    return members.filter(member => {
      // Search filter
      if (searchQuery && !member.name.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      // Department filter
      if (departmentFilter !== 'all' && member.department !== departmentFilter) {
        return false;
      }
      // Status filter
      if (statusFilter !== 'all' && member.status !== statusFilter) {
        return false;
      }
      return true;
    });
  }, [members, searchQuery, departmentFilter, statusFilter]);

  // Group members by department
  const groupedMembers = useMemo(() => {
    const hasDepartments = departments.length > 0;
    if (!hasDepartments) {
      return { 'All Team Members': filteredMembers };
    }

    const groups = {};
    filteredMembers.forEach(member => {
      const dept = member.department || 'No Department';
      if (!groups[dept]) groups[dept] = [];
      groups[dept].push(member);
    });
    return groups;
  }, [filteredMembers, departments]);

  const toggleDept = (dept) => {
    setCollapsedDepts(prev => ({ ...prev, [dept]: !prev[dept] }));
  };

  // No organization state - employees see different message than admins
  if (!organization && !loading) {
    const isAdmin = user?.role === 'admin';
    
    return (
      <Layout>
        <div className="space-y-8" data-testid="team-page-no-org">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">
              {t('team.title')}
            </h1>
            <p className="text-slate-500 mt-1">{t('team.members')}</p>
          </div>

          <div className="premium-card max-w-md mx-auto">
            <div className="empty-state">
              <div className="empty-state-icon">
                <Building2 className="w-10 h-10" />
              </div>
              <h3 className="empty-state-title">No Organization Yet</h3>
              {isAdmin ? (
                <>
                  <p className="empty-state-text">Create an organization to start inviting team members</p>
                  <Button 
                    className="btn-premium gap-2" 
                    onClick={() => setShowCreateOrgDialog(true)}
                    data-testid="create-org-btn"
                  >
                    <Building2 className="w-4 h-4" />
                    Create Organization
                  </Button>
                </>
              ) : (
                <p className="empty-state-text">Contact your admin to be added to an organization</p>
              )}
            </div>
          </div>

          {/* Create Org Dialog - only for admins */}
          {isAdmin && (
            <Dialog open={showCreateOrgDialog} onOpenChange={setShowCreateOrgDialog}>
              <DialogContent className="sm:max-w-md rounded-2xl">
                <DialogHeader>
                  <DialogTitle className="text-xl">Create Organization</DialogTitle>
                  <DialogDescription>Start your own organization and invite team members</DialogDescription>
                </DialogHeader>
                <div className="space-y-5 pt-4">
                  <div className="space-y-2">
                    <Label className="label-premium">Organization Name</Label>
                    <Input
                      placeholder="Acme Inc."
                      value={orgName}
                      onChange={(e) => setOrgName(e.target.value)}
                      className="input-premium"
                      data-testid="org-name-input"
                    />
                  </div>
                  <Button 
                    onClick={handleCreateOrg} 
                    className="w-full btn-premium h-11"
                    data-testid="save-org-btn"
                  >
                    Create Organization
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </Layout>
    );
  }

  // Empty team state
  if (members.length === 0 && !loading) {
    return (
      <Layout>
        <div className="space-y-8" data-testid="team-page-empty">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">
                Team
              </h1>
              <p className="text-slate-500 mt-1">{organization?.name}</p>
            </div>
          </div>

          <div className="premium-card max-w-md mx-auto">
            <div className="empty-state">
              <div className="empty-state-icon">
                <Users className="w-10 h-10" />
              </div>
              <h3 className="empty-state-title">Your team is empty</h3>
              <p className="empty-state-text">Contact your admin to add team members</p>
              
              {canInvite && (
                <Button 
                  className="btn-premium gap-2 mt-4" 
                  onClick={() => setShowInviteDialog(true)}
                  data-testid="invite-member-btn-empty"
                >
                  <UserPlus className="w-4 h-4" />
                  Invite Member
                </Button>
              )}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <TooltipProvider delayDuration={300}>
        <div className="space-y-6" data-testid="team-page">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">
                Team
              </h1>
              <p className="text-slate-500 mt-1">{organization?.name} · {members.length} member{members.length !== 1 ? 's' : ''}</p>
            </div>
            
            {canInvite && (
              <Button 
                className="btn-premium h-11 px-6" 
                onClick={() => setShowInviteDialog(true)}
                data-testid="invite-member-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Invite Member
              </Button>
            )}
          </div>

          {/* Currently Working Section */}
          {workingMembers.length > 0 && (
            <div className="premium-card p-5" data-testid="currently-working-section">
              <div className="flex items-center gap-2 mb-4">
                <div className="relative">
                  <Clock className="w-5 h-5 text-emerald-600" />
                  <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-emerald-50 dark:bg-emerald-900/30 rounded-full animate-pulse" />
                </div>
                <h2 className="text-base font-semibold text-slate-900 dark:text-white">
                  Currently Working
                </h2>
                <span className="text-xs text-slate-500">({workingMembers.length})</span>
              </div>
              
              <div className="flex flex-wrap gap-3">
                {workingMembers.map((member, idx) => (
                  <div 
                    key={member.id}
                    className="flex items-center gap-3 px-4 py-3 bg-emerald-50 dark:bg-emerald-900/10 rounded-xl border border-emerald-100 dark:border-emerald-500/20"
                    data-testid={`working-member-${member.id}`}
                  >
                    <Avatar className="h-9 w-9 ring-2 ring-emerald-200 dark:ring-emerald-500/30">
                      <AvatarFallback 
                        className="text-white text-xs font-semibold"
                        style={{ backgroundColor: getColorForIndex(idx) }}
                      >
                        {getInitials(member.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">
                        {member.name} {member.is_self && <span className="text-xs text-slate-400">(You)</span>}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <span className="font-medium text-emerald-600 dark:text-emerald-400">
                          {formatElapsedTime(member.start_time)}
                        </span>
                        <span>·</span>
                        {member.project_name ? (
                          <span className="flex items-center gap-1">
                            <div 
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: member.project_color || '#94a3b8' }}
                            />
                            {member.project_name}
                          </span>
                        ) : (
                          <span className="text-slate-400 italic">Working on a project</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 input-premium"
                data-testid="team-search-input"
              />
              {searchQuery && (
                <button 
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Department Filter */}
            {departments.length > 0 && (
              <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                <SelectTrigger className="w-48 h-11 rounded-xl" data-testid="department-filter">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-slate-400" />
                    <SelectValue placeholder="Department" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  {departments.map(dept => (
                    <SelectItem key={dept.name} value={dept.name}>
                      {dept.name} ({dept.count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 h-11 rounded-xl" data-testid="status-filter">
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-slate-400" />
                  <SelectValue placeholder="Status" />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="online">
                  <div className="flex items-center gap-2">
                    <Circle className="w-2 h-2 fill-emerald-500 text-emerald-500" />
                    Online
                  </div>
                </SelectItem>
                <SelectItem value="away">
                  <div className="flex items-center gap-2">
                    <Circle className="w-2 h-2 fill-amber-500 text-amber-500" />
                    Away
                  </div>
                </SelectItem>
                <SelectItem value="offline">
                  <div className="flex items-center gap-2">
                    <Circle className="w-2 h-2 fill-slate-400 text-slate-400" />
                    Offline
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Team Members Grid - Grouped by Department */}
          {Object.entries(groupedMembers).map(([deptName, deptMembers]) => (
            <Collapsible 
              key={deptName}
              open={!collapsedDepts[deptName]}
              onOpenChange={() => toggleDept(deptName)}
            >
              {departments.length > 0 && (
                <CollapsibleTrigger className="flex items-center gap-2 w-full text-left mb-4 group">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-300">
                    {collapsedDepts[deptName] ? (
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-slate-400" />
                    )}
                    <Building2 className="w-4 h-4 text-slate-400" />
                    {deptName}
                    <span className="text-xs font-normal text-slate-400">({deptMembers.length})</span>
                  </div>
                </CollapsibleTrigger>
              )}
              
              <CollapsibleContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="members-grid">
                  {deptMembers.map((member, idx) => (
                    <div 
                      key={member.id}
                      onClick={() => handleViewProfile(member)}
                      className="premium-card p-5 cursor-pointer hover:shadow-lg hover:border-blue-200 dark:hover:border-blue-500/30 transition-all duration-200 group"
                      data-testid={`member-card-${member.id}`}
                    >
                      <div className="flex items-start gap-4">
                        {/* Avatar with status indicator */}
                        <div className="relative">
                          <Avatar className="h-12 w-12 ring-2 ring-white dark:ring-slate-800 shadow-md">
                            <AvatarFallback 
                              className="text-white text-sm font-semibold"
                              style={{ backgroundColor: getColorForIndex(members.indexOf(member)) }}
                            >
                              {getInitials(member.name)}
                            </AvatarFallback>
                          </Avatar>
                          {member.status_visible && (
                            <div 
                              className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white dark:border-slate-800 ${getStatusColor(member.status)}`}
                              title={getStatusText(member.status)}
                            />
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="text-sm font-semibold text-slate-900 dark:text-white truncate">
                              {member.name}
                            </h3>
                            {member.is_self && (
                              <span className="text-xs text-slate-400">(You)</span>
                            )}
                          </div>
                          
                          {member.job_title && (
                            <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
                              <Briefcase className="w-3 h-3" />
                              {member.job_title}
                            </p>
                          )}
                          
                          {/* Status Badge */}
                          <div className="mt-2">
                            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${
                              member.status === 'online' 
                                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400' 
                                : member.status === 'away'
                                ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400'
                                : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
                            }`}>
                              <Circle className={`w-1.5 h-1.5 ${
                                member.status === 'online' ? 'fill-emerald-500' :
                                member.status === 'away' ? 'fill-amber-500' : 'fill-slate-400'
                              }`} />
                              {getStatusText(member.status)}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Shared Projects Dots */}
                      {member.shared_project_count > 0 && (
                        <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800">
                          <div className="flex items-center gap-1.5">
                            <FolderKanban className="w-3.5 h-3.5 text-slate-400" />
                            <span className="text-xs text-slate-500">Shared projects:</span>
                            <div className="flex items-center gap-1 ml-1">
                              {member.shared_projects.slice(0, 3).map((project) => (
                                <Tooltip key={project.id}>
                                  <TooltipTrigger>
                                    <div 
                                      className="w-4 h-4 rounded-full border-2 border-white dark:border-slate-800 shadow-sm"
                                      style={{ backgroundColor: project.color }}
                                    />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p className="text-xs">You both work on <span className="font-medium">{project.name}</span></p>
                                  </TooltipContent>
                                </Tooltip>
                              ))}
                              {member.shared_project_count > 3 && (
                                <span className="text-xs text-slate-400 ml-1">
                                  +{member.shared_project_count - 3} more
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          ))}

          {/* No results */}
          {filteredMembers.length === 0 && (
            <div className="premium-card">
              <div className="empty-state">
                <div className="empty-state-icon">
                  <Search className="w-10 h-10" />
                </div>
                <h3 className="empty-state-title">No team members found</h3>
                <p className="empty-state-text">Try adjusting your search or filters</p>
                <Button 
                  variant="outline" 
                  onClick={() => { setSearchQuery(''); setDepartmentFilter('all'); setStatusFilter('all'); }}
                  className="mt-2"
                >
                  Clear filters
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Invite Dialog */}
        <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
          <DialogContent className="sm:max-w-md rounded-2xl">
            <DialogHeader>
              <DialogTitle className="text-xl">Invite Team Member</DialogTitle>
              <DialogDescription>Send an invitation to join your organization</DialogDescription>
            </DialogHeader>
            <div className="space-y-5 pt-4">
              <div className="space-y-2">
                <Label className="label-premium">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="email"
                    placeholder="colleague@company.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className="pl-10 input-premium"
                    data-testid="invite-email-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="label-premium">Role</Label>
                <Select value={inviteRole} onValueChange={setInviteRole}>
                  <SelectTrigger className="h-11 rounded-xl" data-testid="invite-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="employee">Employee</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    {user?.role === 'admin' && <SelectItem value="admin">Admin</SelectItem>}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="label-premium">Personal Message (Optional)</Label>
                <Textarea
                  placeholder="Add a personal note to your invitation..."
                  value={inviteMessage}
                  onChange={(e) => setInviteMessage(e.target.value)}
                  className="input-premium min-h-[80px] resize-none"
                  data-testid="invite-message-input"
                />
              </div>
              
              <Button 
                onClick={handleInvite} 
                className="w-full btn-premium h-11"
                data-testid="send-invite-btn"
              >
                Send Invitation
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Profile Sheet */}
        <Sheet open={showProfileSheet} onOpenChange={setShowProfileSheet}>
          <SheetContent className="sm:max-w-md">
            <SheetHeader>
              <SheetTitle>Team Member Profile</SheetTitle>
            </SheetHeader>
            {selectedMember && (
              <div className="mt-6 space-y-6">
                {/* Avatar and Name */}
                <div className="flex items-center gap-4">
                  <Avatar className="h-16 w-16 ring-4 ring-slate-100 dark:ring-slate-800 shadow-lg">
                    <AvatarFallback 
                      className="text-white text-xl font-semibold"
                      style={{ backgroundColor: COLORS[0] }}
                    >
                      {getInitials(selectedMember.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                      {selectedMember.name}
                    </h2>
                    {selectedMember.job_title && (
                      <p className="text-sm text-slate-500">{selectedMember.job_title}</p>
                    )}
                  </div>
                </div>

                {/* Info Cards */}
                <div className="space-y-3">
                  {selectedMember.department && (
                    <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                      <Building2 className="w-5 h-5 text-slate-400" />
                      <div>
                        <p className="text-xs text-slate-500">Department</p>
                        <p className="text-sm font-medium text-slate-900 dark:text-white">{selectedMember.department}</p>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                    <Briefcase className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500">Role</p>
                      <p className="text-sm font-medium text-slate-900 dark:text-white capitalize">{selectedMember.role}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                    <Circle className={`w-5 h-5 ${
                      selectedMember.status === 'online' ? 'fill-emerald-500 text-emerald-500' :
                      selectedMember.status === 'away' ? 'fill-amber-500 text-amber-500' : 'fill-slate-400 text-slate-400'
                    }`} />
                    <div>
                      <p className="text-xs text-slate-500">Status</p>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">{getStatusText(selectedMember.status)}</p>
                    </div>
                  </div>
                </div>

                {/* Shared Projects */}
                <div>
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                    <FolderKanban className="w-4 h-4 text-slate-400" />
                    Shared Projects
                  </h3>
                  {selectedMember.shared_projects?.length > 0 ? (
                    <div className="space-y-2">
                      {selectedMember.shared_projects.map(project => (
                        <div 
                          key={project.id}
                          className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl"
                        >
                          <div 
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: project.color }}
                          />
                          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{project.name}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 italic">No shared projects</p>
                  )}
                </div>
              </div>
            )}
          </SheetContent>
        </Sheet>
      </TooltipProvider>
    </Layout>
  );
};

export default Team;
